import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, MessageCircle, Edit, Calendar, Activity, Camera, Phone, Mail, Clock, Star, Loader2, AlertCircle } from 'lucide-react'
import { useCoachStore } from '@/stores/coachStore'
import { apiService } from '@/services/api'
import type { Client } from '@/types'
import { EditClientModal } from '@/components/coach/EditClientModal'
import { EditGoalsModal } from '@/components/coach/EditGoalsModal'
import { AddMeasurementModal } from '@/components/coach/AddMeasurementModal'

export function ClientDetailPage() {
  const { clientId } = useParams()
  const navigate = useNavigate()
  const { setCurrentPage } = useCoachStore()
  
  const [client, setClient] = useState<Client | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false)
  const [isEditGoalsModalOpen, setIsEditGoalsModalOpen] = useState(false)
  const [isAddMeasurementModalOpen, setIsAddMeasurementModalOpen] = useState(false)

  const loadClient = async () => {
    if (!clientId) return
    
    try {
      setIsLoading(true)
      setError(null)
      
      console.log('🚀 Fetching client details for ID:', clientId)
      
      const response = await apiService.getClientById(clientId)
      
      console.log('📦 Client API response:', response)
      
      if (response.success && response.data) {
        // Transform API client to match frontend Client type
        const apiClient = response.data
        const transformedClient: Client = {
          id: apiClient.id,
          userId: apiClient.userId,
          name: `${apiClient.user?.firstName || ''} ${apiClient.user?.lastName || ''}`.trim() || apiClient.user?.email || 'Unknown',
          email: apiClient.user?.email || '',
          phoneNumber: apiClient.user?.phoneNumber || undefined,
          profilePhoto: apiClient.user?.profilePicture || undefined,
          dateOfBirth: apiClient.user?.dateOfBirth ? new Date(apiClient.user.dateOfBirth) : undefined,
          gender: apiClient.user?.gender as any,
          
          // Coaching relationship
          coachId: apiClient.assignedCoachId || '',
          assignedDate: new Date(apiClient.createdAt),
          status: (apiClient.paymentStatus === 'COMPLETED' ? 'active' : 
                 apiClient.onboardingProgress < 100 ? 'onboarding' : 'inactive') as any,
          
          // Goals (convert from string[] to ClientGoal[] if needed)
          goals: (apiClient.goals || []).map((goal: string, index: number) => ({
            id: `goal-${index}`,
            type: 'custom' as const,
            title: goal,
            target: { type: 'boolean' as const, value: false },
            priority: 'medium' as const,
            status: 'active' as const,
            progress: 0,
            milestones: [],
            createdAt: new Date()
          })),
          
          // Default/placeholder data
          currentProgram: undefined,
          measurements: [],
          progressPhotos: [],
          
          // Activity defaults
          lastActivity: new Date(apiClient.updatedAt),
          responseRate: 90,
          preferredContactMethod: 'email' as any,
          
          // Quick stats
          quickStats: {
            weeklyCompliance: Math.floor(Math.random() * 100),
            progressTrend: 'improving',
            engagementLevel: 'medium' as const,
            daysWithCoach: Math.floor((Date.now() - new Date(apiClient.createdAt).getTime()) / (1000 * 60 * 60 * 24)),
            currentStreak: 0,
            totalWorkouts: 0,
            averageRating: 0
          },
          
          createdAt: new Date(apiClient.createdAt),
          updatedAt: new Date(apiClient.updatedAt)
        }
        
        setClient(transformedClient)
      } else {
        throw new Error(response.error || 'Failed to load client')
      }
    } catch (err: any) {
      console.error('❌ Failed to load client:', err)
      setError(err.message || 'Failed to load client details')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setCurrentPage('clients')
    loadClient()
  }, [clientId, setCurrentPage])

  // Loading state
  if (isLoading) {
    return (
      <div className="coach-section text-center py-12">
        <div className="flex items-center justify-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Loading client details...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !client) {
    return (
      <div className="coach-section text-center py-12">
        <div className="mb-4">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">
            {error || 'Client not found'}
          </p>
          <p className="text-sm text-gray-500">
            Client ID: {clientId}
          </p>
        </div>
        <div className="space-x-2">
          <button 
            onClick={loadClient} 
            className="coach-button-secondary"
          >
            Try Again
          </button>
          <button 
            onClick={() => navigate('/clients')} 
            className="coach-button-primary"
          >
            Back to Clients
          </button>
        </div>
      </div>
    )
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return <span className="status-active">Active</span>
      case 'onboarding': return <span className="status-warning">Onboarding</span>
      case 'inactive': return <span className="status-inactive">Inactive</span>
      default: return <span className="status-badge bg-gray-100 text-gray-800">{status}</span>
    }
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="coach-header">
        <div className="flex items-center space-x-4">
          <button onClick={() => navigate('/clients')} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-600 font-semibold text-lg">
                {client.name.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
              <div className="flex items-center space-x-4 text-gray-600">
                <span>{client.email}</span>
                {getStatusBadge(client.status)}
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex space-x-2">
          <button className="coach-button-secondary px-4 py-2">
            <MessageCircle className="w-4 h-4 mr-2" />
            Message
          </button>
          <button onClick={() => setIsEditClientModalOpen(true)} className="coach-button-primary px-4 py-2">
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </button>
        </div>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="coach-card text-center">
          <div className="text-2xl font-bold text-primary-600">{client.quickStats.weeklyCompliance}%</div>
          <div className="text-sm text-gray-600">Weekly Compliance</div>
          <div className="progress-bar mt-2">
            <div className="progress-fill" style={{ width: `${client.quickStats.weeklyCompliance}%` }} />
          </div>
        </div>
        
        <div className="coach-card text-center">
          <div className="text-2xl font-bold text-gray-900">{client.quickStats.currentStreak}</div>
          <div className="text-sm text-gray-600">Current Streak (days)</div>
          <div className="flex items-center justify-center mt-1">
            <Activity className="w-4 h-4 text-success-500" />
          </div>
        </div>
        
        <div className="coach-card text-center">
          <div className="text-2xl font-bold text-gray-900">{client.quickStats.daysWithCoach}</div>
          <div className="text-sm text-gray-600">Days with Coach</div>
          <div className="flex items-center justify-center mt-1">
            <Calendar className="w-4 h-4 text-primary-500" />
          </div>
        </div>
        
        <div className="coach-card text-center">
          <div className="flex items-center justify-center space-x-1">
            <span className="text-2xl font-bold text-gray-900">{client.quickStats.averageRating}</span>
            <Star className="w-5 h-5 text-warning-500 fill-current" />
          </div>
          <div className="text-sm text-gray-600">Average Rating</div>
          <div className="flex items-center justify-center mt-1">
            <span className="text-xs text-gray-500">({client.quickStats.totalWorkouts} workouts)</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="coach-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
              <button onClick={() => setIsEditClientModalOpen(true)} className="text-primary-600 hover:text-primary-700 text-sm">
                Edit
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-600">Email</div>
                  <div className="font-medium">{client.email}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Phone className="w-4 h-4 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-600">Phone</div>
                  <div className="font-medium">{client.phoneNumber || 'Not provided'}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Calendar className="w-4 h-4 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-600">Date of Birth</div>
                  <div className="font-medium">
                    {client.dateOfBirth ? formatDate(client.dateOfBirth) : 'Not provided'}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Clock className="w-4 h-4 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-600">Assigned Date</div>
                  <div className="font-medium">{formatDate(client.assignedDate)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Current Program */}
          {client.currentProgram && (
            <div className="coach-section">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Program</h2>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{client.currentProgram.name}</h3>
                    <p className="text-sm text-gray-600">{client.currentProgram.description}</p>
                  </div>
                  <span className="text-xs px-2 py-1 bg-primary-100 text-primary-800 rounded-full">
                    {client.currentProgram.difficulty}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-gray-600">Duration</div>
                    <div className="font-medium">{client.currentProgram.duration} weeks</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Frequency</div>
                    <div className="font-medium">{client.currentProgram.workoutsPerWeek}x/week</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Start Date</div>
                    <div className="font-medium">{formatDate(client.currentProgram.startDate)}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Progress</div>
                    <div className="font-medium">{client.currentProgram.progress.completionRate}%</div>
                  </div>
                </div>
                
                <div className="mt-3">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${client.currentProgram.progress.completionRate}%` }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Goals */}
          <div className="coach-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Goals</h2>
              <button onClick={() => setIsEditGoalsModalOpen(true)} className="text-primary-600 hover:text-primary-700 text-sm">
                Edit Goals
              </button>
            </div>
            
            <div className="space-y-4">
              {client.goals.map((goal) => (
                <div key={goal.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">{goal.title}</h3>
                      <p className="text-sm text-gray-600">{goal.description}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        goal.priority === 'high' ? 'bg-danger-100 text-danger-800' :
                        goal.priority === 'medium' ? 'bg-warning-100 text-warning-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {goal.priority}
                      </span>
                      <span className="text-xs px-2 py-1 bg-success-100 text-success-800 rounded-full">
                        {goal.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-600">Progress</span>
                    <span className="font-medium">{goal.progress}%</span>
                  </div>
                  
                  <div className="progress-bar mb-3">
                    <div className="progress-fill" style={{ width: `${goal.progress}%` }} />
                  </div>
                  
                  {goal.deadline && (
                    <div className="text-xs text-gray-500">
                      Target: {formatDate(goal.deadline)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Progress Trend */}
          <div className="coach-section">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress Overview</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Trend</span>
                <div className="flex items-center space-x-2">
                  <span>{client.quickStats.progressTrend === 'improving' ? '📈' : client.quickStats.progressTrend === 'declining' ? '📉' : '➡️'}</span>
                  <span className="font-medium capitalize">{client.quickStats.progressTrend}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Engagement</span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  client.quickStats.engagementLevel === 'high' ? 'bg-success-100 text-success-800' :
                  client.quickStats.engagementLevel === 'medium' ? 'bg-warning-100 text-warning-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {client.quickStats.engagementLevel}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Response Rate</span>
                <span className="font-medium">{client.responseRate}%</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Last Activity</span>
                <span className="font-medium text-sm">{formatDate(client.lastActivity)}</span>
              </div>
            </div>
          </div>

          {/* Recent Measurements */}
          <div className="coach-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Measurements</h3>
              <button onClick={() => setIsAddMeasurementModalOpen(true)} className="text-primary-600 hover:text-primary-700 text-sm">
                Add
              </button>
            </div>
            
            <div className="space-y-3">
              {client.measurements.slice(0, 5).map((measurement) => (
                <div key={measurement.id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 capitalize">{measurement.type.replace('-', ' ')}</span>
                  <span className="font-medium">{measurement.value} {measurement.unit}</span>
                </div>
              ))}
              {client.measurements.length === 0 && (
                <p className="text-sm text-gray-500">No measurements recorded</p>
              )}
            </div>
          </div>

          {/* Progress Photos */}
          <div className="coach-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Progress Photos</h3>
              <button className="text-primary-600 hover:text-primary-700 text-sm">
                <Camera className="w-4 h-4" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {client.progressPhotos.slice(0, 4).map((photo) => (
                <div key={photo.id} className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                  <Camera className="w-8 h-8 text-gray-400" />
                </div>
              ))}
              {client.progressPhotos.length === 0 && (
                <div className="col-span-2 text-center py-8 text-gray-500">
                  <Camera className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No progress photos</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <EditClientModal
        client={client}
        isOpen={isEditClientModalOpen}
        onClose={() => setIsEditClientModalOpen(false)}
        onSave={(updatedClient) => {
          setClient(updatedClient)
          setIsEditClientModalOpen(false)
        }}
      />
      
      <EditGoalsModal
        client={client}
        isOpen={isEditGoalsModalOpen}
        onClose={() => setIsEditGoalsModalOpen(false)}
        onSave={(updatedClient) => {
          setClient(updatedClient)
          setIsEditGoalsModalOpen(false)
        }}
      />
      
      <AddMeasurementModal
        client={client}
        isOpen={isAddMeasurementModalOpen}
        onClose={() => setIsAddMeasurementModalOpen(false)}
        onSave={(updatedClient) => {
          setClient(updatedClient)
          setIsAddMeasurementModalOpen(false)
        }}
      />
    </div>
  )
}